import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { getTeacherCohorts } from '@/lib/db'
import {
  ArrowLeft,
  Video,
  Users,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  UserCheck,
  UserX,
  Search,
  Save,
  Loader2
} from 'lucide-react'

interface WorkshopDetails {
  id: string
  title: string
  description: string
  scheduled_date: string
  duration_minutes: number
  unit: { order_num: number; title: string }
}

interface StudentRow {
  id: string
  profile_id: string
  profile: {
    first_name: string
    last_name: string
    email: string
    avatar_url: string | null
  }
  attendance: {
    id: string
    status: 'attended' | 'absent' | 'enrolled' | 'pending'
    attended_at: string | null
  } | null
}

export default function WorkshopAttendance() {
  const { workshopId } = useParams()
  const { teacher, profile } = useAuth()
  const [workshop, setWorkshop] = useState<WorkshopDetails | null>(null)
  const [students, setStudents] = useState<StudentRow[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [changes, setChanges] = useState<Record<string, 'attended' | 'absent'>>({})

  useEffect(() => {
    if (workshopId && profile?.id) {
      loadWorkshopData()
    }
  }, [workshopId, profile?.id])

  const loadWorkshopData = async () => {
    try {
      // 1. Cargar detalles del taller (incluyendo cohort_id)
      const { data: workshopData, error: workshopError } = await (supabase
        .from('unit_workshops') as any)
        .select(`
          id,
          title,
          description,
          scheduled_date,
          duration_minutes,
          cohort_id,
          unit:units!unit_id(order_num, title)
        `)
        .eq('id', workshopId as string)
        .single()

      if (workshopError) throw workshopError
      setWorkshop(workshopData as any)

      // 2. Cargar alumnos de la cohorte del taller
      let allStudents: any[] = []
      let studentProfileIds: string[] = []

      if (workshopData.cohort_id) {
        // Usar la cohorte específica del taller
        const { data: cohortData } = await (supabase
          .from('cohorts') as any)
          .select('student_ids')
          .eq('id', workshopData.cohort_id)
          .single()

        studentProfileIds = cohortData?.student_ids || []
      }

      // Fallback: si no hay cohorte en el taller, usar cohortes del profesor
      if (studentProfileIds.length === 0 && profile?.id) {
        const { data: cohorts } = await getTeacherCohorts(profile.id)
        studentProfileIds = [...new Set((cohorts || []).flatMap(c => c.student_ids || []))]
      }

      if (studentProfileIds.length > 0) {
        const { data: studentsData, error: studentsError } = await (supabase
          .from('students') as any)
          .select(`
            id,
            profile_id,
            profile:profiles!profile_id(first_name, last_name, email, avatar_url)
          `)
          .in('profile_id', studentProfileIds)
          .order('created_at', { ascending: true })

        if (studentsError) throw studentsError
        allStudents = studentsData || []
      }

      // 3. Cargar registros de asistencia para este taller (con datos del alumno)
      const { data: attendanceData } = await (supabase
        .from('workshop_attendance') as any)
        .select(`
          id, student_id, status, attended_at,
          student:students!student_id(
            id, profile_id,
            profile:profiles!profile_id(first_name, last_name, email, avatar_url)
          )
        `)
        .eq('workshop_id', workshopId as string)

      // 4. Cruzar datos: cada estudiante de la cohorte con su asistencia
      const attendanceByStudent: Record<string, any> = {}
      if (attendanceData) {
        for (const a of attendanceData) {
          attendanceByStudent[a.student_id] = a
        }
      }

      const addedStudentIds = new Set<string>()
      const merged: StudentRow[] = []

      // 4a. Alumnos de la cohorte (aparecen siempre, con o sin asistencia)
      for (const s of (allStudents || [])) {
        addedStudentIds.add(s.id)
        const att = attendanceByStudent[s.id]
        merged.push({
          id: s.id,
          profile_id: s.profile_id,
          profile: s.profile,
          attendance: att
            ? { id: att.id, status: att.status, attended_at: att.attended_at }
            : null
        })
      }

      // 4b. Alumnos con asistencia que NO estén en la cohorte
      for (const att of (attendanceData || [])) {
        if (addedStudentIds.has(att.student_id)) continue
        addedStudentIds.add(att.student_id)
        const profile = (att.student as any)?.profile
        merged.push({
          id: att.student_id,
          profile_id: att.student?.profile_id || '',
          profile: profile || { first_name: 'Desconocido', last_name: '', email: '', avatar_url: null },
          attendance: { id: att.id, status: att.status, attended_at: att.attended_at }
        })
      }

      // Ordenar: primero los que asistieron, luego los que no, alfabéticamente
      merged.sort((a, b) => {
        const aAttended = a.attendance?.status === 'attended' ? 0 : 1
        const bAttended = b.attendance?.status === 'attended' ? 0 : 1
        if (aAttended !== bAttended) return aAttended - bAttended
        const aName = `${a.profile?.first_name} ${a.profile?.last_name}`.toLowerCase()
        const bName = `${b.profile?.first_name} ${b.profile?.last_name}`.toLowerCase()
        return aName.localeCompare(bName)
      })

      setStudents(merged)
    } catch (error: any) {
      console.error('❌ Error loading workshop data:', error?.message || error?.code || JSON.stringify(error))
    } finally {
      setIsLoading(false)
    }
  }

  const handleAttendanceChange = (studentId: string, status: 'attended' | 'absent') => {
    setChanges(prev => ({
      ...prev,
      [studentId]: status
    }))
  }

  const saveAttendance = async () => {
    setIsSaving(true)
    try {
      const updates = Object.entries(changes).map(async ([studentId, status]) => {
        const student = students.find(s => s.id === studentId)

        if (student?.attendance) {
          // Actualizar registro existente
          return (supabase.from('workshop_attendance') as any)
            .update({
              status,
              marked_by: teacher?.id,
              marked_at: new Date().toISOString(),
              attended_at: status === 'attended' ? new Date().toISOString() : null
            })
            .eq('id', student.attendance.id)
        } else {
          // Crear nuevo registro
          return (supabase.from('workshop_attendance') as any)
            .insert({
              workshop_id: workshopId,
              student_id: studentId,
              status,
              marked_by: teacher?.id,
              marked_at: new Date().toISOString(),
              attended_at: status === 'attended' ? new Date().toISOString() : null
            })
        }
      })

      await Promise.all(updates)

      // Recargar datos
      await loadWorkshopData()
      setChanges({})

      alert('Asistencia guardada correctamente')
    } catch (error) {
      console.error('Error saving attendance:', error)
      alert('Error al guardar la asistencia')
    } finally {
      setIsSaving(false)
    }
  }

  const markAllAs = (status: 'attended' | 'absent') => {
    const newChanges: Record<string, 'attended' | 'absent'> = {}
    students.forEach(s => {
      newChanges[s.id] = status
    })
    setChanges(newChanges)
  }

  const getAttendanceStatus = (student: StudentRow): 'attended' | 'absent' => {
    if (changes[student.id]) {
      return changes[student.id]
    }
    // Si el alumno tiene registro con status 'attended' → asistió
    // Si no tiene registro o tiene otro status → no asistió
    return student.attendance?.status === 'attended' ? 'attended' : 'absent'
  }

  const filteredStudents = students.filter(s =>
    s.profile?.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.profile?.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.profile?.email?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const stats = {
    total: students.length,
    attended: students.filter(s => getAttendanceStatus(s) === 'attended').length,
    absent: students.filter(s => getAttendanceStatus(s) === 'absent').length
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    )
  }

  if (!workshop) {
    return (
      <div className="text-center py-12">
        <Video className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">Taller no encontrado</p>
        <Link to="/teacher/workshops" className="text-blue-600 hover:underline text-sm mt-2 inline-block">
          Volver a talleres
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/teacher/workshops"
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Control de Asistencia</h1>
          <p className="text-gray-600">{workshop.title}</p>
        </div>
        {Object.keys(changes).length > 0 && (
          <button
            onClick={saveAttendance}
            disabled={isSaving}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <Save className="w-5 h-5" />
            {isSaving ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        )}
      </div>

      {/* Workshop Info */}
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <div className="flex items-start gap-4">
          <div className="p-3 rounded-lg bg-indigo-100">
            <Video className="w-6 h-6 text-indigo-600" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900">{workshop.title}</h2>
            <p className="text-sm text-gray-500 mb-4">
              Unidad {workshop.unit?.order_num}: {workshop.unit?.title}
            </p>
            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{new Date(workshop.scheduled_date).toLocaleDateString('es-ES', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{workshop.duration_minutes} minutos</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4" />
                <span>{students.length} estudiantes</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-sm text-gray-500">Total Alumnos</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-100">
              <UserCheck className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{stats.attended}</p>
              <p className="text-sm text-gray-500">Asistieron</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-100">
              <UserX className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{stats.absent}</p>
              <p className="text-sm text-gray-500">No Asistieron</p>
            </div>
          </div>
        </div>
      </div>

      {/* Actions & Search */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar estudiante..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => markAllAs('attended')}
            className="px-4 py-2 text-green-700 bg-green-100 rounded-lg hover:bg-green-200 transition-colors text-sm font-medium"
          >
            Marcar todos como asistidos
          </button>
          <button
            onClick={() => markAllAs('absent')}
            className="px-4 py-2 text-red-700 bg-red-100 rounded-lg hover:bg-red-200 transition-colors text-sm font-medium"
          >
            Marcar todos como no asistidos
          </button>
        </div>
      </div>

      {/* Attendance List */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estudiante
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Asistencia
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredStudents.length > 0 ? (
                filteredStudents.map((student) => {
                  const status = getAttendanceStatus(student)
                  const hasChanges = changes[student.id] !== undefined
                  const selfMarked = student.attendance?.status === 'attended' && student.attendance?.attended_at

                  return (
                    <tr key={student.id} className={hasChanges ? 'bg-yellow-50' : 'hover:bg-gray-50'}>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">
                            {student.profile?.avatar_url ? (
                              <img src={student.profile.avatar_url} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-gray-500 font-medium text-sm">
                                {student.profile?.first_name?.[0]}{student.profile?.last_name?.[0]}
                              </span>
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {student.profile?.first_name} {student.profile?.last_name}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-600 text-sm">
                        {student.profile?.email}
                      </td>
                      <td className="px-6 py-4 text-center">
                        {status === 'attended' ? (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            <CheckCircle className="w-3.5 h-3.5" />
                            Asistió
                            {selfMarked && !hasChanges && (
                              <span className="text-green-500 ml-1">(auto)</span>
                            )}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            <XCircle className="w-3.5 h-3.5" />
                            No asistió
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => handleAttendanceChange(student.id, 'attended')}
                            className={`p-2 rounded-lg transition-colors ${
                              status === 'attended'
                                ? 'bg-green-500 text-white'
                                : 'bg-gray-100 text-gray-400 hover:bg-green-100 hover:text-green-600'
                            }`}
                            title="Marcar presente"
                          >
                            <CheckCircle className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleAttendanceChange(student.id, 'absent')}
                            className={`p-2 rounded-lg transition-colors ${
                              status === 'absent'
                                ? 'bg-red-500 text-white'
                                : 'bg-gray-100 text-gray-400 hover:bg-red-100 hover:text-red-600'
                            }`}
                            title="Marcar ausente"
                          >
                            <XCircle className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })
              ) : (
                <tr>
                  <td colSpan={4} className="px-6 py-12 text-center">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-gray-500">
                      {searchTerm ? 'No se encontraron estudiantes' : 'No hay estudiantes activos'}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pending Changes Warning */}
      {Object.keys(changes).length > 0 && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-yellow-100 border border-yellow-300 rounded-lg px-6 py-3 shadow-lg flex items-center gap-4">
          <p className="text-yellow-800 text-sm font-medium">
            {Object.keys(changes).length} cambio(s) sin guardar
          </p>
          <button
            onClick={saveAttendance}
            disabled={isSaving}
            className="px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium disabled:opacity-50"
          >
            {isSaving ? 'Guardando...' : 'Guardar'}
          </button>
        </div>
      )}
    </div>
  )
}
